import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { getAzureQueueApps, getAzureQueueTopics } from 'config';
import './AzureQueue.css';
import { useDiagnosticsAuth } from 'pages/Diagnostics/context/DiagnosticsAuthContext';
import CountWidget from '../components/CountWidget';
import { BOOLEAN_OPTIONS } from 'utils/constants/selectorOptions';
import centralApi from 'services/centralApi';
import { API_ENDPOINTS } from 'utils/constants';
import Spinner from 'components/Spinner/Spinner';
import NoData from 'components/NoData/NoData';
import { CONSTANTS } from 'utils/constants/constants';


interface AzureCountData {
  appid: string;
  name: string;
  topic: string;
  index: number;
  count: number;
}


const AzureQueue: React.FC = () => {
  const allApps = getAzureQueueApps();
  const topics = getAzureQueueTopics();
  const { token } = useDiagnosticsAuth();
  const [widgets, setWidgets] = useState<AzureCountData[]>([]);
  const [loading, setLoading] = useState(BOOLEAN_OPTIONS.FALSE);

  // Identify config app (first app from the list)
  const configApp = allApps.length > 0 ? allApps[0] : null;
  const configAppId = configApp?.appid || '';

  // Prepare request payload
  const prepareRequestPayload = () => {
    const payload = [];
    
    // For topics ending with '-', use all apps
    const dashTopics = topics.filter(topic => topic.endsWith('-'));
    for (const topic of dashTopics) {
      for (const app of allApps) {
        payload.push({ appid: app.appid, topic });
      }
    }
    
    // For other topics, use only config app
    const nonDashTopics = topics.filter(topic => !topic.endsWith('-'));
    for (const topic of nonDashTopics) {
      if (configAppId) {
        payload.push({ appid: configAppId, topic });
      }
    }
    
    return payload;
  };

  // Fetch counts from API
  const fetchCounts = async () => {
    try {
      setLoading(BOOLEAN_OPTIONS.TRUE);
      if (!token) return;

      const requestPayload = prepareRequestPayload();
      if (requestPayload.length === 0) return;

      const payload = {
        args: JSON.stringify(requestPayload),
        token
      };

      // // Fetch data from API
      const result = await centralApi(
        'GET', 
        API_ENDPOINTS.DIAGNOSTICS_GET_AZURE_QUEUE_COUNT, 
        null, 
        payload
      );

      // // Transform results
      const transformedResults = result
        // Filter out items below threshold
        .filter((item: any) => item.count > CONSTANTS.THRESHOLD)
        // Sort by count descending
        .sort((a: any, b: any) => b.count - a.count)
        // Map to widget format
        .map((item: any, index: number) => {
          // For non-dash topics, use topic name as widget name
          const name = item.topic.endsWith('-') 
            ? allApps.find((a:any) => a.appid === item.appid)?.name || item.appid
            : item.topic;


             const appid = item.topic.endsWith('-') 
            ? allApps.find((a:any) => a.appid === item.appid)?.appid || item.appid
            : "";
          
          return {
            appid,
            name,
            topic:item.topic,
            index,
            count: item.count
          };
        });

      setWidgets(transformedResults);
    } catch (error) {
      console.error('Failed to fetch queue counts:', error);
    } finally {
      setLoading(BOOLEAN_OPTIONS.FALSE);
    }
  };

   useEffect(() => {
          let interval: NodeJS.Timeout;
          fetchCounts();
          interval = setInterval(fetchCounts, 30000);
          return () => clearInterval(interval);
      }, []);


  return (
    <>
    <div className="mb-4 azure-queue-container">
      <h6>{t("azureQueueDiagnosticsLabel")}</h6>
      <p className="azure-queue-description-headings">
        {t("azureQueueDescriptionLabel")}
      </p>
    </div>

    {loading ? (
      <Spinner />
    ) : (
      <div className="diagnostics-widgets-row">
        {widgets.length === 0 ? (
           <NoData />
        ) : (
          widgets.map((widget) => (
            <CountWidget
              key={`${widget.appid}-${widget.topic}`}
              appid={widget.appid}
              name={widget.name}
              topic={widget.topic}
              index={widget.index}
              count={widget.count}
            />
          ))
        )}
      </div>
    )}
  </>
  );
};

export default AzureQueue;